/**
 * React Hook for Bluetooth Operations
 * Connects BLE service to Zustand store
 */

import { useCallback } from 'react'
import { useMScaleStore } from '../store'
import { bluetoothService, BluetoothService } from '../services/bluetooth/BluetoothService'
import { BLEError } from '../services/bluetooth/types'

export function useBluetooth() {
  const {
    isConnected,
    isConnecting,
    connectionError,
    setConnected,
    setConnecting,
    setConnectionError,
    setConnectionProgress,
    setDeviceInfo,
    setWeights,
    setGroups,
    setSystemStatus,
    reset,
  } = useMScaleStore()

  /**
   * Connect to M-Scale device
   */
  const connect = useCallback(async () => {
    if (!bluetoothService.isConnected() && !isConnecting) {
      setConnecting(true)
      setConnectionError(null)

      const TOTAL_STEPS = 7
      let currentStep = 0

      const updateProgress = (step: string) => {
        currentStep++
        const progress = { step, current: currentStep, total: TOTAL_STEPS }
        setConnectionProgress(progress)
      }

      // Step 1: Show initial progress before picker (this will be visible)
      updateProgress('Select device from picker')

      try {
        // Step 2: Connect to device (this shows browser picker and blocks)
        const state = await bluetoothService.connect()
        updateProgress('Connected to device')
        // Don't call setConnected yet - keep isConnecting=true until fully done

        // Step 3: Read device info
        updateProgress('Reading device info')
        const deviceInfo = await bluetoothService.readDeviceInfo()
        setDeviceInfo(
          deviceInfo.name,
          deviceInfo.firmwareVersion,
          deviceInfo.hardwareConfig
        )

        // Step 4: Read initial data (parallelized)
        updateProgress('Loading scale data')
        const [weights, groupsData, targetRange, groupSize, minRatio] = await Promise.all([
          bluetoothService.readWeights(),
          bluetoothService.readGroups(),
          bluetoothService.readTargetRange(),
          bluetoothService.readGroupSize(),
          bluetoothService.readMinRatio()
        ])

        setWeights(weights)
        setGroups(groupsData.groups, groupsData.count)
        useMScaleStore.getState().setTargetRange(targetRange)
        useMScaleStore.getState().setGroupSize(groupSize)
        useMScaleStore.getState().setMinRatio(minRatio)

        // Step 5: Subscribe to critical updates (parallelized)
        updateProgress('Setting up live updates')
        await Promise.all([
          bluetoothService.subscribeToWeights((weights) => {
            setWeights(weights)
          }),
          bluetoothService.subscribeToGroups((groups, count) => {
            setGroups(groups, count)
          }),
          bluetoothService.subscribeToStatus((status) => {
            setSystemStatus(status)
          }),
          bluetoothService.subscribeToOTAStatus((status) => {
            useMScaleStore.getState().setOTAStatus(status)
          })
        ])

        // Step 6: Setup dual-device mode (non-critical, don't block)
        updateProgress('Finalizing connection')
        bluetoothService.subscribeToDeviceLinkStatus((status) => {
          useMScaleStore.getState().setDeviceLinkStatus(status)
        }).then(() => {
          return bluetoothService.readDeviceLinkStatus()
        }).then((status) => {
          useMScaleStore.getState().setDeviceLinkStatus(status)
        }).catch((error) => {
          console.warn('Failed to setup device link status:', error)
        })

        // Sync time (non-critical, don't block)
        bluetoothService.syncTime().catch((error) => {
          console.warn('Failed to sync time:', error)
        })

        // Step 7: Complete - NOW we're fully connected
        updateProgress('Connected')

        // Set connected state with device name
        setConnected(true, state.deviceName || undefined)

        // Handle disconnection
        bluetoothService.onDisconnect(() => {
          setConnected(false)
          reset()
        })

        // Clear progress and connecting state after a short delay
        setTimeout(() => {
          setConnectionProgress(null)
          setConnecting(false)
        }, 500)

      } catch (error) {
        console.error('Connection error:', error)
        if (error instanceof BLEError) {
          setConnectionError(error.message)
        } else {
          setConnectionError('Failed to connect to device')
        }
        setConnecting(false)
        setConnectionProgress(null)
      }
    }
  }, [isConnecting, setConnecting, setConnectionError, setConnectionProgress, setConnected, setDeviceInfo, setWeights, setGroups, setSystemStatus, reset])

  /**
   * Disconnect from device
   */
  const disconnect = useCallback(async () => {
    try {
      await bluetoothService.disconnect()
      setConnected(false)
      reset()
    } catch (error) {
      console.error('Disconnect error:', error)
    }
  }, [setConnected, reset])

  /**
   * Tare all scales
   */
  const tareAll = useCallback(async () => {
    try {
      await bluetoothService.tare(0xFF)
    } catch (error) {
      console.error('Tare error:', error)
      throw error
    }
  }, [])

  /**
   * Tare specific position
   */
  const tarePosition = useCallback(async (position: number) => {
    try {
      await bluetoothService.tare(position)
    } catch (error) {
      console.error('Tare position error:', error)
      throw error
    }
  }, [])

  /**
   * Update target range
   */
  const updateTargetRange = useCallback(async (min: number, max: number) => {
    try {
      await bluetoothService.writeTargetRange({ min, max })
      useMScaleStore.getState().setTargetRange({ min, max })
    } catch (error) {
      console.error('Update target range error:', error)
      throw error
    }
  }, [])

  /**
   * Update group size
   */
  const updateGroupSize = useCallback(async (size: number) => {
    try {
      await bluetoothService.writeGroupSize(size)
      useMScaleStore.getState().setGroupSize(size)
    } catch (error) {
      console.error('Update group size error:', error)
      throw error
    }
  }, [])

  /**
   * Update minimum ratio
   */
  const updateMinRatio = useCallback(async (ratio: number) => {
    try {
      await bluetoothService.writeMinRatio(ratio)
      useMScaleStore.getState().setMinRatio(ratio)
    } catch (error) {
      console.error('Update min ratio error:', error)
      throw error
    }
  }, [])

  /**
   * Start calibration for a position
   */
  const startCalibration = useCallback(async (position: number) => {
    try {
      const { CalibrationCommand } = await import('../services/bluetooth/types')
      await bluetoothService.sendCalibrationCommand(position, CalibrationCommand.START_ZERO)
    } catch (error) {
      console.error('Start calibration error:', error)
      throw error
    }
  }, [])

  /**
   * Complete zero calibration step
   */
  const completeZero = useCallback(async (position: number) => {
    try {
      const { CalibrationCommand } = await import('../services/bluetooth/types')
      await bluetoothService.sendCalibrationCommand(position, CalibrationCommand.COMPLETE_ZERO)
    } catch (error) {
      console.error('Complete zero error:', error)
      throw error
    }
  }, [])

  /**
   * Start span calibration with known weight
   */
  const startSpan = useCallback(async (position: number, knownWeight: number) => {
    try {
      const { CalibrationCommand } = await import('../services/bluetooth/types')
      await bluetoothService.sendCalibrationCommand(position, CalibrationCommand.START_SPAN, knownWeight)
    } catch (error) {
      console.error('Start span error:', error)
      throw error
    }
  }, [])

  /**
   * Complete span calibration
   */
  const completeSpan = useCallback(async (position: number) => {
    try {
      const { CalibrationCommand } = await import('../services/bluetooth/types')
      await bluetoothService.sendCalibrationCommand(position, CalibrationCommand.COMPLETE_SPAN)
    } catch (error) {
      console.error('Complete span error:', error)
      throw error
    }
  }, [])

  /**
   * Cancel calibration
   */
  const cancelCalibration = useCallback(async (position: number) => {
    try {
      const { CalibrationCommand } = await import('../services/bluetooth/types')
      await bluetoothService.sendCalibrationCommand(position, CalibrationCommand.CANCEL)
    } catch (error) {
      console.error('Cancel calibration error:', error)
      // Don't throw - cancellation should fail silently
    }
  }, [])

  /**
   * Set LED for a position (white for preview/hover, temporary)
   */
  const setLEDWhite = useCallback(async (position: number, durationMs: number = 500) => {
    try {
      await bluetoothService.sendLEDOverride({
        position,
        r: 255,
        g: 255,
        b: 255,
        duration: Math.floor(durationMs / 100), // Convert to 100ms units
      })
    } catch (error) {
      console.error('Set LED white error:', error)
      // Don't throw for hover effects - fail silently
    }
  }, [])

  /**
   * Clear LED override (return to normal operation)
   */
  const clearLED = useCallback(async () => {
    try {
      await bluetoothService.sendLEDOverride({
        position: 0xFF, // All positions
        r: 0,
        g: 0,
        b: 0,
        duration: 0, // Immediate
      })
    } catch (error) {
      console.error('Clear LED error:', error)
      // Don't throw - fail silently
    }
  }, [])

  /**
   * Sync time with device for LED animation alignment
   */
  const syncTime = useCallback(async () => {
    try {
      await bluetoothService.syncTime()
    } catch (error) {
      console.error('Sync time error:', error)
      throw error
    }
  }, [])

  /**
   * Read custom device name suffix
   */
  const readCustomSuffix = useCallback(async () => {
    try {
      return await bluetoothService.readCustomSuffix()
    } catch (error) {
      console.error('Failed to read custom suffix:', error)
      throw error
    }
  }, [])

  /**
   * Write custom device name suffix
   */
  const writeCustomSuffix = useCallback(async (suffix: string) => {
    try {
      await bluetoothService.writeCustomSuffix(suffix)
    } catch (error) {
      console.error('Failed to write custom suffix:', error)
      throw error
    }
  }, [])

  /**
   * Check if Bluetooth is supported
   */
  const isSupported = BluetoothService.isSupported()

  return {
    isSupported,
    isConnected,
    isConnecting,
    connectionError,
    connect,
    disconnect,
    tareAll,
    tarePosition,
    updateTargetRange,
    updateGroupSize,
    updateMinRatio,
    startCalibration,
    completeZero,
    startSpan,
    completeSpan,
    cancelCalibration,
    setLEDWhite,
    clearLED,
    syncTime,
    readCustomSuffix,
    writeCustomSuffix,
  }
}
